import { useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { DownloadSimple, Image as ImageIcon, Copy, ShareNetwork, Check, ArrowSquareOut } from "@phosphor-icons/react";
import { waitForRender, loadBrandLogoDataUrl } from "../../lib/brandAssets";

/**
 * CardActions — downloadable + shareable actions for a branded QR card.
 *
 * Reliability notes:
 *  - Preloads the brand logo as a base64 data URL before rasterizing so the
 *    canvas is never tainted by cross-origin images.
 *  - Waits for `document.fonts.ready` + 2 RAFs + tiny timeout before capture
 *    so Cormorant Garamond / Manrope are rendered in the snapshot.
 *  - Wraps every action in try/catch and surfaces the actual error message
 *    to the user — no silent failures.
 *
 * Action priorities:
 *   PRIMARY    → Download PNG
 *   SECONDARY  → Download SVG · Copy · Share · Open in new tab
 */
export default function CardActions({
  targetRef,
  filenameBase = "social-circle-qr",
  svgElementId,
  shareTitle = "The Social Circle",
  shareText = "Bringing people and venues together.",
  shareUrl,
  layout = "row",
  testIdPrefix = "card-actions",
}) {
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);

  /** Render the target node to a PNG dataURL. Throws with a useful message. */
  const renderPng = async () => {
    const node = targetRef?.current;
    if (!node) throw new Error("QR card not mounted yet");
    // Preload logo + fonts so the snapshot is pixel-accurate
    await loadBrandLogoDataUrl();
    await waitForRender(80);
    return await toPng(node, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#050505",
      style: { background: "#050505" },
    });
  };

  const dataUrlToBlob = async (dataUrl) => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ---- PRIMARY: Download PNG ----
  const onDownloadPng = async () => {
    setBusy("png");
    try {
      const dataUrl = await renderPng();
      triggerDownload(dataUrl, `${filenameBase}.png`);
      toast.success("PNG downloaded");
    } catch (e) {
      console.error("PNG export failed:", e);
      toast.error("Download failed. Please try again.");
    } finally { setBusy(null); }
  };

  // ---- SECONDARY: Download SVG (bare QR vector) ----
  const onDownloadSvg = () => {
    if (!svgElementId) { toast.error("No SVG source for this card"); return; }
    const svg = document.getElementById(svgElementId);
    if (!svg) { toast.error("QR not ready"); return; }
    setBusy("svg");
    try {
      const xml = new XMLSerializer().serializeToString(svg);
      const blob = new Blob(['<?xml version="1.0" standalone="no"?>\r\n', xml], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      triggerDownload(url, `${filenameBase}.svg`);
      URL.revokeObjectURL(url);
      toast.success("SVG downloaded");
    } catch (e) {
      console.error("SVG export failed:", e);
      toast.error("SVG export failed. Please try again.");
    } finally { setBusy(null); }
  };

  // ---- Copy to clipboard ----
  const onCopy = async () => {
    setBusy("copy");
    try {
      if (!navigator.clipboard || !window.ClipboardItem) {
        throw new Error("Clipboard images not supported in this browser");
      }
      const dataUrl = await renderPng();
      const blob = await dataUrlToBlob(dataUrl);
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      toast.success("Image copied to clipboard");
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.error("Copy failed:", e);
      toast.error("Copy not supported here — use Download instead.");
    } finally { setBusy(null); }
  };

  // ---- Share via Web Share API ----
  const onShare = async () => {
    setBusy("share");
    try {
      const dataUrl = await renderPng();
      const blob = await dataUrlToBlob(dataUrl);
      const file = new File([blob], `${filenameBase}.png`, { type: "image/png" });
      const payload = { title: shareTitle, text: shareText, files: [file] };
      if (shareUrl) payload.url = shareUrl;
      if (navigator.canShare && navigator.canShare(payload)) {
        await navigator.share(payload);
      } else if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } else if (shareUrl) {
        await navigator.clipboard?.writeText(shareUrl);
        toast.success("Link copied to clipboard");
      } else {
        triggerDownload(dataUrl, `${filenameBase}.png`);
        toast.message("Sharing not supported — image downloaded instead");
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("Share failed:", e);
      toast.error("Share failed. Please try again.");
    } finally { setBusy(null); }
  };

  // ---- Fallback: Open the rendered image in a new tab so users can manually save ----
  const onOpenInNewTab = async () => {
    setBusy("open");
    try {
      const dataUrl = await renderPng();
      const w = window.open();
      if (!w) { toast.error("Pop-up blocked. Allow pop-ups to use this option."); return; }
      w.document.title = filenameBase;
      w.document.body.style.margin = "0";
      w.document.body.style.background = "#050505";
      w.document.body.style.display = "flex";
      w.document.body.style.alignItems = "center";
      w.document.body.style.justifyContent = "center";
      w.document.body.style.minHeight = "100vh";
      const img = w.document.createElement("img");
      img.src = dataUrl;
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      w.document.body.appendChild(img);
    } catch (e) {
      console.error("Open in new tab failed:", e);
      toast.error("Could not open preview. Please try again.");
    } finally { setBusy(null); }
  };

  const Btn = ({ onClick, icon, label, primary, testId, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy !== null}
      className={primary
        ? "tsc-btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-60"
        : "tsc-btn-outline inline-flex items-center justify-center gap-2 disabled:opacity-60"
      }
      data-testid={testId}
    >
      {icon} <span>{label}</span>
    </button>
  );

  const wrapperClass = layout === "grid"
    ? "grid grid-cols-2 gap-2 w-full"
    : "flex flex-wrap items-center justify-center gap-2";

  return (
    <div className={wrapperClass} data-testid={testIdPrefix}>
      {/* PRIMARY action — always first */}
      <Btn primary onClick={onDownloadPng} icon={<DownloadSimple size={14} />} label={busy === "png" ? "Saving…" : "Download QR"} testId={`${testIdPrefix}-png`} />

      {/* Secondary actions */}
      {svgElementId && (
        <Btn onClick={onDownloadSvg} icon={<ImageIcon size={14} />} label={busy === "svg" ? "Saving…" : "Download SVG"} testId={`${testIdPrefix}-svg`} />
      )}
      <Btn onClick={onCopy} icon={copied ? <Check size={14} /> : <Copy size={14} />} label={busy === "copy" ? "Copying…" : (copied ? "Copied" : "Copy image")} testId={`${testIdPrefix}-copy`} />
      <Btn onClick={onShare} icon={<ShareNetwork size={14} />} label={busy === "share" ? "Sharing…" : "Share"} testId={`${testIdPrefix}-share`} />
      <Btn onClick={onOpenInNewTab} icon={<ArrowSquareOut size={14} />} label={busy === "open" ? "Opening…" : "Open preview"} testId={`${testIdPrefix}-open`} />
    </div>
  );
}
